import { Component, Input, forwardRef, inject, signal } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { LocalizationService } from '../../core/localization/localization.service';
import { AssetMaster, CatalogService } from '../../features/catalog/catalog.service';

interface AssetOption {
  id: string;
  label: string;
}

@Component({
  standalone: true,
  selector: 'ain-asset-selector',
  imports: [FormsModule, SelectModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AssetSelectorComponent),
      multi: true
    }
  ],
  template: `
    <p-select
      class="selector"
      [options]="options()"
      optionLabel="label"
      optionValue="id"
      [placeholder]="t('catalog.assets.selectPlaceholder')"
      [showClear]="true"
      [filter]="true"
      filterBy="label,id"
      [disabled]="disabled || loading()"
      [loading]="loading()"
      [ngModel]="value()"
      (ngModelChange)="onValueChange($event)"
      [appendTo]="appendTo" />
  `,
  styles: [`
    .selector { width: 100%; }
  `]
})
export class AssetSelectorComponent implements ControlValueAccessor {
  private readonly api = inject(CatalogService);
  private readonly localization = inject(LocalizationService);
  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};

  readonly loading = signal(false);
  readonly options = signal<AssetOption[]>([]);
  readonly value = signal<string | null>(null);

  @Input() appendTo: 'body' | 'self' = 'body';
  @Input() statusFilter: string | undefined;
  disabled = false;

  constructor() {
    this.load();
  }

  writeValue(value: string | null): void {
    this.value.set(value?.trim() || null);
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
  }

  onValueChange(value: string | null): void {
    this.value.set(value);
    this.onChange(value);
    this.onTouched();
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private load(): void {
    this.loading.set(true);
    this.api.listAssets(this.statusFilter).subscribe({
      next: assets => {
        this.options.set(assets.map(asset => ({ id: asset.id, label: this.buildLabel(asset) })));
        if (this.value() && !assets.some(item => item.id === this.value())) {
          this.onValueChange(null);
        }
        this.loading.set(false);
      },
      error: () => {
        this.options.set([]);
        this.loading.set(false);
      }
    });
  }

  private buildLabel(asset: AssetMaster): string {
    return `${asset.assetNumber} — ${asset.name}`;
  }
}
